import React, { memo, useState, useEffect } from "react";
import styled from "styled-components";
import { TextField } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { ContinueButton } from "../../continue-button/continue-button";
import { inviteFriends } from "../../../requests/booking.requests";
import { useErrorHandler } from "react-error-boundary";
import { Title } from "../../title/title";

const ShareReservationWrapper = styled.div`
  color: ${(props) => props.theme.disabledColor};

  #continue-button {
    margin-top: -18px;
    margin-left: 18px;
  }
`;

const ShareReservationContent = styled.div`
  background-color: ${(props) => props.theme.backgroundTransparentWhite};
  border-radius: 0 8px 8px 8px;
  padding: 6px 4px 2px 4px;
`;

const FullElement = styled.div`
  display: flex;
  flex: 0 0 100%;
  max-width: 100%;
  margin-bottom: 4px;
  background-color: ${(props) => props.theme.backgroundColor};
  border-radius: 4px;
`;

const CustomErrorField = styled(TextField)`
  &.error {
    fieldset {
      border-color: ${(props) => props.theme.error};
    }

    p.Mui-required {
      color: ${(props) => props.theme.error};
      margin-top: 0;
    }
  }
`;

const MessageText = styled.div`
  display: flex;
  justify-content: center;
  background-color: ${(props) => props.theme.success};
  color: white;
  padding: 20px 60px;
  border-radius: 5px;
  margin-bottom: 36px;
`;

const Share = () => {
  const { t } = useTranslation();
  const [message, setMessage] = useState<string>("");
  const [messageError, setMessageError] = useState<boolean>(false);
  const [emails, setEmails] = useState<string>("");
  const [emailsError, setEmailsError] = useState<boolean>(false);
  const [formattedEmails, setFormattedEmails] = useState<string[]>([]);
  const [isShared, setIsShared] = useState<boolean>(false);

  const throwError = useErrorHandler();

  useEffect(() => {
    if (formattedEmails.length > 0) {
      const subscription = inviteFriends(message!, formattedEmails).subscribe(
        (resp: any) => {
          setIsShared(true);
        },
        throwError
      );
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [formattedEmails, throwError, message]);

  const shareReservation = () => {
    let error = false;
    if (!message?.length) {
      error = true;
      setMessageError(true);
    }
    if (!emails?.length) {
      error = true;
      setEmailsError(true);
    }
    if (!error) {
      setFormattedEmails(
        emails.split(",").map((formattedEmail: string) => formattedEmail.trim())
      );
    }
  };

  const messageChanged = (
    ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newMessage = ev.target.value;
    updateField(newMessage, setMessage, setMessageError);
  };

  const emailChanged = (
    ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newEmails = ev.target.value;
    updateField(newEmails, setEmails, setEmailsError);
  };

  const updateField = (
    value: string,
    messageCb: (value: React.SetStateAction<string>) => void,
    errorCb: (value: React.SetStateAction<boolean>) => void
  ) => {
    messageCb(value);
    if (value.length) {
      errorCb(false);
    }
  };

  const contentAfterSharing = (
    <MessageText>{t("instabook.bookings.friend_sharing.success")}</MessageText>
  );

  const contentForSharing = (
    <>
      <Title text={t("instabook.booking_sharing.title")} />
      <ShareReservationContent>
        <FullElement>
          <CustomErrorField
            fullWidth={true}
            multiline={true}
            className={messageError ? "error" : ""}
            placeholder={t("instabook.booking_sharing.to_friends")}
            rows="4"
            variant={"outlined" as any}
            onChange={messageChanged}
          />
        </FullElement>
        <FullElement>
          <CustomErrorField
            fullWidth={true}
            multiline={true}
            className={emailsError ? "error" : ""}
            placeholder={t("instabook.booking_sharing.send_to_friends")}
            rows="1"
            variant={"outlined" as any}
            onChange={emailChanged}
          />
        </FullElement>
      </ShareReservationContent>
      <ContinueButton
        onClick={shareReservation}
        buttonTextKey={t("instabook.booking_sharing.invite")}
      />
    </>
  );

  return (
    <ShareReservationWrapper data-test="share-reservation">
      {isShared ? contentAfterSharing : contentForSharing}
    </ShareReservationWrapper>
  );
};

export const ShareReservation = memo(Share);
